import React, { FC } from 'react';
import { Route, Routes } from 'react-router';

import { DashboardPage } from './pages/dashboard';
import { ContractDetailPage } from './pages/contract-detail';

import { useAuth } from '@dwelvio/auth-react';

import { SignUpPage } from './pages/signup';
import { appPaths } from './app-paths';
import { UnitListPage } from './pages/unit-list';
import { ContractListPage } from './pages/contract-list';
import { InvoiceListPage } from './pages/invoice-list';
import { MoneyTransactionListPage } from './pages/money-transaction-list';
import { SplashPage } from './pages/splash';
import { IUserData } from './types';
import { SignInPage } from './pages/signin/signin-page';
import { AccountPage } from './pages/account';
import { ProfilePage } from './pages/profile';
import { InvitationPage } from './pages/invitation';
import { TaskListPage } from './pages/task-list';
import { UnitDetailPage } from './pages/unit-detail';
import { TaskDetailPage } from './pages/task-detail';
import { PasswordForgotPage } from './pages/password-forgot';
import { PasswordResetPage } from './pages/password-reset';
import { NotificationSettingsPage } from './pages/notification-settings/notification-settings-page';
import { VerifyEmailPage } from './pages/verify-email';
import { InvoiceDetailPage } from './pages/invoice-detail';
import { RecurringInvoiceDetailPage } from './pages/recurring-invoice-detail';
import { DocumentPage } from './pages/document';
import { ExpenseListPage } from './pages/expense-list';
import { ExpenseDetailPage } from './pages/expense-detail';

export function AppRouter() {
  return (
    <Routes>
      <Route
        path={appPaths.dashboard()}
        element={
          <RequireAuth>
            <UnitListPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.unitList.path()}
        element={
          <RequireAuth>
            <UnitListPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.unit.path()}
        element={
          <RequireAuth>
            <UnitDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.contractList.path()}
        element={
          <RequireAuth>
            <ContractListPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.invoiceList.path()}
        element={
          <RequireAuth>
            <InvoiceListPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.invoice.path()}
        element={
          <RequireAuth>
            <InvoiceDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.expenseList.path()}
        element={
          <RequireAuth>
            <ExpenseListPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.expense.path()}
        element={
          <RequireAuth>
            <ExpenseDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.recurringInvoice.path()}
        element={
          <RequireAuth>
            <RecurringInvoiceDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.moneyTransactionList.path()}
        element={
          <RequireAuth>
            <MoneyTransactionListPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.contract.path()}
        element={
          <RequireAuth>
            <ContractDetailPage />
          </RequireAuth>
        }
      />

      <Route
        path={appPaths.account.path()}
        element={
          <RequireAuth>
            <AccountPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.profile.path()}
        element={
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.notificationSettings.path()}
        element={
          <RequireAuth>
            <NotificationSettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.taskList.path()}
        element={
          <RequireAuth>
            <TaskListPage />
          </RequireAuth>
        }
      />
      <Route
        path={appPaths.task.path()}
        element={
          <RequireAuth>
            <TaskDetailPage />
          </RequireAuth>
        }
      />

      <Route
        path={appPaths.document.path()}
        element={
          <RequireAuth>
            <DocumentPage />
          </RequireAuth>
        }
      />

      <Route path={appPaths.signin.path()} element={<SignInPage />} />
      <Route path={appPaths.signup.path()} element={<SignUpPage />} />
      <Route path={appPaths.invitation.path()} element={<InvitationPage />} />
      <Route path={appPaths.passwordForgot.path()} element={<PasswordForgotPage />} />
      <Route path={appPaths.passwordReset.path()} element={<PasswordResetPage />} />
      <Route path={appPaths.emailVerification.path()} element={<VerifyEmailPage />} />
      <Route path={appPaths.verifyEmail.path()} element={<VerifyEmailPage />} />

      {/* Fallback route for any unmatched paths */}

      <Route path="*" element={<h1>Not Found</h1>} />
    </Routes>
  );
}

const RequireAuth: FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth<IUserData>();

  if (!isAuthenticated) {
    if (isLoading) {
      return <SplashPage />;
    }
    return <SignInPage />;
  }
  return children;
};
